/**
 * WYSIWYG-editor voor notities (Joris, 1 sep 2026) — Outline-achtig schrijfgevoel
 * (TipTap) met ónze opslag: de inhoud gaat als markdown de database in, zodat de
 * bestaande weergave (react-markdown) en het Airtable-archief compatibel blijven.
 */
import React, { useRef } from 'react';
import { Box, Divider, ToggleButton, Tooltip } from '@mui/material';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import toast from 'react-hot-toast';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Image from '@tiptap/extension-image';
import { Markdown } from 'tiptap-markdown';
import { absoluteAfbeeldingsUrl, afbeeldingenUit, uploadNotitieAfbeelding } from '../utils/notitieAfbeeldingen';

// Afbeeldingen (Joris, 7 sep): plakken, slepen of kiezen → upload naar blob → `![](/Api/…)`
// in de markdown. In de editor tonen we de absolute backend-URL, opgeslagen blijft hij relatief.
const NotitieAfbeelding = Image.extend({
  renderHTML({ HTMLAttributes }) {
    return ['img', { ...HTMLAttributes, src: absoluteAfbeeldingsUrl(HTMLAttributes.src),
      style: 'max-width:100%;height:auto;border-radius:6px;display:block;margin:6px 0' }];
  },
});

async function voegAfbeeldingenIn(editor, files) {
  for (const file of files) {
    const id = toast.loading(`Afbeelding uploaden… (${Math.round(file.size / 1024)} kB)`);
    try {
      const url = await uploadNotitieAfbeelding(file);
      editor.chain().focus().setImage({ src: url, alt: file.name || '' }).run();
      toast.success('Afbeelding toegevoegd', { id });
    } catch (e) { toast.error(e.message || 'Afbeelding uploaden mislukt', { id }); }
  }
}

function Knop({ actief, onClick, title, children }) {
  return (
    <Tooltip title={title}>
      <ToggleButton value="x" selected={!!actief} size="small"
        onMouseDown={(e) => { e.preventDefault(); onClick(); }}
        sx={{ border: 0, borderRadius: 1, px: 0.75, py: 0.25, minWidth: 30,
          '&.Mui-selected': { bgcolor: '#e0e7ff', color: '#3730a3' } }}>
        {children}
      </ToggleButton>
    </Tooltip>
  );
}

export default function NotitieEditor({
  value = '',
  onChange,
  placeholder = 'Typ hier…',
  minHeight = 120,
  autoFocus = false,
  afbeeldingen = true, // uit voor velden die als platte markdown elders landen (callsheet → Airtable/PDF)
}) {
  const bestandRef = useRef(null);
  const editorRef = useRef(null); // voor de paste/drop-handlers (die krijgen de editor niet mee)
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Placeholder.configure({ placeholder }),
      NotitieAfbeelding.configure({ inline: false, allowBase64: false }),
      Markdown.configure({ html: false, linkify: true, breaks: true }),
    ],
    content: value,
    autofocus: autoFocus ? 'end' : false,
    editorProps: {
      // Geplakte of gesleepte afbeeldingen zelf afhandelen; tekst gaat gewoon door.
      handlePaste: (view, event) => {
        if (!afbeeldingen) return false;
        const files = afbeeldingenUit(event.clipboardData);
        if (!files.length) return false;
        event.preventDefault();
        voegAfbeeldingenIn(editorRef.current, files);
        return true;
      },
      handleDrop: (view, event) => {
        if (!afbeeldingen) return false;
        const files = afbeeldingenUit(event.dataTransfer);
        if (!files.length) return false;
        event.preventDefault();
        voegAfbeeldingenIn(editorRef.current, files);
        return true;
      },
    },
    onUpdate: ({ editor: ed }) => {
      onChange?.(ed.storage.markdown.getMarkdown());
    },
  });

  editorRef.current = editor;

  // Extern leeggemaakt (na opslaan): editor mee leegmaken zonder cursor-gedoe
  // tijdens het typen (alleen reageren als de teksten echt uiteenlopen).
  React.useEffect(() => {
    if (!editor) return;
    const huidige = editor.storage.markdown.getMarkdown();
    if (value === '' && huidige !== '') { editor.commands.clearContent(); return; }
    // Sjabloon of meeting ingevoegd via een knop (editor heeft dan geen focus):
    // inhoud overnemen en de cursor achteraan zetten. Tijdens het typen nooit.
    if (value !== huidige && !editor.isFocused) editor.commands.setContent(value, false);
  }, [value, editor]);

  if (!editor) return null;

  return (
    <Box sx={{
      border: '1px solid #cbd5e1', borderRadius: 1.5, bgcolor: '#fff',
      '&:focus-within': { borderColor: '#6366f1', boxShadow: '0 0 0 2px rgba(99,102,241,0.15)' },
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, px: 0.75, py: 0.4, borderBottom: '1px solid #f1f5f9', flexWrap: 'wrap' }}>
        <Knop title="Vet (⌘B)" actief={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()}>
          <FormatBoldIcon sx={{ fontSize: 17 }} />
        </Knop>
        <Knop title="Cursief (⌘I)" actief={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()}>
          <FormatItalicIcon sx={{ fontSize: 17 }} />
        </Knop>
        <Knop title="Doorhalen" actief={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()}>
          <StrikethroughSIcon sx={{ fontSize: 17 }} />
        </Knop>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        <Knop title="Kop" actief={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
          <Box component="span" sx={{ fontWeight: 800, fontSize: 14 }}>H</Box>
        </Knop>
        <Knop title="Subkop" actief={editor.isActive('heading', { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>
          <Box component="span" sx={{ fontWeight: 700, fontSize: 12 }}>h</Box>
        </Knop>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        <Knop title="Opsomming" actief={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()}>
          <FormatListBulletedIcon sx={{ fontSize: 17 }} />
        </Knop>
        <Knop title="Genummerde lijst" actief={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()}>
          <FormatListNumberedIcon sx={{ fontSize: 17 }} />
        </Knop>
        <Knop title="Citaat" actief={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()}>
          <FormatQuoteIcon sx={{ fontSize: 17 }} />
        </Knop>
        {afbeeldingen && (
          <>
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
            <Knop title="Afbeelding invoegen (of plak / sleep hem in de tekst)" onClick={() => bestandRef.current?.click()}>
              <AddPhotoAlternateIcon sx={{ fontSize: 17 }} />
            </Knop>
            <input ref={bestandRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp" multiple hidden
              onChange={(e) => { const files = Array.from(e.target.files || []); e.target.value = ''; if (files.length) voegAfbeeldingenIn(editor, files); }} />
          </>
        )}
      </Box>
      <Box sx={{
        px: 1.5, py: 1, minHeight, cursor: 'text',
        '& .ProseMirror': { outline: 'none', minHeight: minHeight - 20, fontSize: '0.9rem', lineHeight: 1.65 },
        '& .ProseMirror p': { m: 0, mb: 0.5 },
        '& .ProseMirror h2': { fontSize: '1.05rem', m: 0, mt: 1, mb: 0.5 },
        '& .ProseMirror h3': { fontSize: '0.95rem', m: 0, mt: 0.75, mb: 0.5 },
        '& .ProseMirror ul, & .ProseMirror ol': { m: 0, pl: 3 },
        '& .ProseMirror blockquote': { borderLeft: '3px solid #e2e8f0', pl: 1.5, ml: 0, color: '#64748b' },
        '& .ProseMirror img': { maxWidth: '100%', height: 'auto', borderRadius: 1 },
        '& .ProseMirror img.ProseMirror-selectednode': { outline: '2px solid #6366f1' },
        '& .ProseMirror p.is-editor-empty:first-of-type::before': {
          content: 'attr(data-placeholder)', color: '#94a3b8', float: 'left', height: 0, pointerEvents: 'none',
        },
      }} onClick={() => editor.chain().focus().run()}>
        <EditorContent editor={editor} />
      </Box>
    </Box>
  );
}
